import { Vehicle } from '@esportal/portal-service/src/models/vehicles';
import {
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Text,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { ChevronRight, Close } from '@volkswagen-onehub/icons-core';
import { InteractionLayerSizeV2, LayerHandleV2, ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';
import React from 'react';
import styled from 'styled-components';
import { PortalContext } from '../../../context/PortalContext';
import { Environment } from '../../../enviroment';
import { actionRedirect } from '../../../helpers/common-helpers';
import { useNotificationsTracker } from '../hooks/use-notifications-tracker';
import { VehicleNotification, VehicleNotificationType } from '../notifications-types';
import { getNotificationMessage, getNotificationTag } from '../notificatios-helpers';

const NotificationContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	padding: var(--size-dynamic0100) 0;
	position: relative;
	cursor: pointer;
	border-bottom: 1px solid #dfe4e8;

	& > svg {
		min-width: fit-content;
		min-height: fit-content;
	}

	&::before {
		content: '';
		position: absolute;
		top: calc(var(--size-dynamic0100) + 3px);
		left: -18px;
		width: 10px;
		height: 10px;
		border-radius: 50%;
		background-color: rgb(26, 216, 77);
	}
`;

export function HeaderNotificationsLayer({
	isLayerOpen,
	handleClose,
	notifications,
}: {
	isLayerOpen: boolean;
	handleClose: () => void;
	notifications: VehicleNotification[];
}) {
	const { layerManager, navigationService, userInteractionService } = React.useContext(PortalContext);
	const layerRef = React.useRef<LayerHandleV2<{}> | null>(null);
	const { trackLayerLoad, trackLayerCloseClick, trackNotificationClick } = useNotificationsTracker();

	const handleLayerClose = () => {
		trackLayerCloseClick();
		handleClose();
	};

	const handleNotificationClick = async (vehicle: Vehicle | undefined, notificationType: VehicleNotificationType) => {
		const notificationTag = getNotificationTag(notificationType);
		trackNotificationClick(notificationTag, vehicle);
		if (notificationType === VehicleNotificationType.Mantenimiento && vehicle) {
			const environmentPath = Environment.getInstance().maintenancePage as string;
			const search = window.location.search;
			const target = navigationService.navigateTo(environmentPath, {});

			if (target) {
				target.push();
				const href = target.createHref();
				addParamToUrl(href, vehicle.frame, search);
			}
		}

		if (notificationType === VehicleNotificationType.PreITV && vehicle) {
			actionRedirect(userInteractionService, 'action:es-cca-loader--open_layer');
		}
	};

	const addParamToUrl = (href: string, param: string, search: string) => {
		const newSearchParams = new URLSearchParams(search);
		newSearchParams.append('frame', param);
		window.history.replaceState(null, '', href + `?${newSearchParams.toString()}`);
	};

	React.useEffect(() => {
		if (layerRef.current || !isLayerOpen) return;

		trackLayerLoad();

		layerRef.current = layerManager.openInteractionLayer(
			() => (
				<Container
					wrap="always"
					padding={ContainerPadding.dynamic0150}
					horizontalAlign="center"
					gutter={ContainerGutter.static400}
				>
					<Container wrap={'never'} horizontalAlign={'space-between'}>
						<Text appearance={TokenTextAppearance.headline400}>Notificaciones</Text>
						<CTA
							tag="button"
							emphasis="tertiary"
							onClick={handleLayerClose}
							aria-label="Close pop-up"
							icon={<Close variant="default" aria-hidden />}
						/>
					</Container>

					<Container wrap="always">
						{notifications.map((notification, index) => (
							<NotificationContainer
								key={`${notification.vehicle.frame}-${notification.notificationType}-${index}`}
								onClick={() => handleNotificationClick(notification.vehicle, notification.notificationType)}
							>
								<Container wrap="always" gutter={ContainerGutter.static150}>
									<Container wrap="always">
										<Text color={TokenTextColor.secondary} bold appearance={TokenTextAppearance.label150}>
											{getNotificationTag(notification.notificationType)}
										</Text>
									</Container>
									<Text bold appearance={TokenTextAppearance.headline100}>
										{getNotificationMessage(notification.notificationType, notification.vehicle.comercialDenomination)}
									</Text>
								</Container>
								<ChevronRight variant="default" aria-hidden />
							</NotificationContainer>
						))}
					</Container>
				</Container>
			),
			{},
			{
				size: InteractionLayerSizeV2.A,
				shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
			},
		);

		return () => {
			layerRef.current?.close();
			layerRef.current = null;
		};
	}, [isLayerOpen, layerManager, trackLayerLoad]);

	return null;
}
